import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
  Badge,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Search as SearchIcon,
  SwapVert as SwapIcon,
  LocalTaxi as TaxiIcon,
  DirectionsCar as CarIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  Schedule as TimeIcon,
  Co2 as Co2Icon,
  PhotoCamera as CameraIcon,
  Upload as UploadIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Pending as PendingIcon,
  Verified as VerifiedIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { FirebaseContext } from 'common';
import { colors } from '../components/Theme/WebTheme';

// Provider configurations - Taxi, Rideshare, VIP only
const PROVIDERS = {
  rydio: {
    name: 'Rydio Taxi',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    icon: TaxiIcon,
    type: 'taxi',
    featured: true,
    bookingUrl: '/addbookings',
  },
  rydio_vip: {
    name: 'Rydio VIP',
    color: '#1e1b4b',
    gradient: 'linear-gradient(135deg, #1e1b4b, #312e81)',
    icon: StarIcon,
    type: 'vip',
    featured: true,
    bookingUrl: '/addbookings',
  },
  uber: {
    name: 'Uber',
    color: '#000000',
    gradient: 'linear-gradient(135deg, #000000, #333333)',
    icon: CarIcon,
    type: 'rideshare',
  },
  uber_xl: {
    name: 'Uber XL',
    color: '#1a1a1a',
    gradient: 'linear-gradient(135deg, #1a1a1a, #404040)',
    icon: CarIcon,
    type: 'rideshare',
  },
  bolt: {
    name: 'Bolt',
    color: '#34D186',
    gradient: 'linear-gradient(135deg, #34D186, #24a667)',
    icon: CarIcon,
    type: 'rideshare',
  },
  bergen_taxi: {
    name: 'Bergen Taxi',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    icon: TaxiIcon,
    type: 'taxi',
  },
  bergen_taxi_vip: {
    name: 'Bergen Taxi VIP',
    color: '#b45309',
    gradient: 'linear-gradient(135deg, #b45309, #92400e)',
    icon: StarIcon,
    type: 'vip',
  },
};

const TRANSPORT_TYPES = {
  taxi: { label: 'Taxi', icon: TaxiIcon },
  rideshare: { label: 'Rideshare', icon: CarIcon },
  vip: { label: 'VIP', icon: StarIcon },
};

export default function TurPrisMatch() {
  const auth = useSelector(state => state.auth);
  const firebase = useContext(FirebaseContext);
  const [tabValue, setTabValue] = useState(0);
  const [origin, setOrigin] = useState('Bergen Lufthavn Flesland');
  const [destination, setDestination] = useState('Bergen sentrum');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Company registration state
  const [companyDialog, setCompanyDialog] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    orgNumber: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    serviceTypes: [],
  });

  // Community submission state
  const [submitDialog, setSubmitDialog] = useState(false);
  const [priceImage, setPriceImage] = useState(null);
  const [extractedPrices, setExtractedPrices] = useState(null);
  const [extracting, setExtracting] = useState(false);

  // Admin state
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [pendingPrices, setPendingPrices] = useState([]);

  // Real-time prices from Firebase
  const [competitorPrices, setCompetitorPrices] = useState({});
  const [rydioPrice, setRydioPrice] = useState(null);

  const isAdmin = auth?.profile?.usertype === 'admin';
  const isFleetAdmin = auth?.profile?.usertype === 'fleetadmin';
  const isCommunity = auth?.profile?.usertype === 'driver' || auth?.profile?.usertype === 'customer';

  // Fetch competitor prices from Firebase
  useEffect(() => {
    if (!firebase || !firebase.database) return;

    const pricesRef = firebase.database.ref('competitor_prices');
    pricesRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const pricesByProvider = {};
        Object.values(data).forEach(entry => {
          if (!pricesByProvider[entry.company]) {
            pricesByProvider[entry.company] = [];
          }
          pricesByProvider[entry.company].push(entry);
        });
        setCompetitorPrices(pricesByProvider);
      }
    });

    return () => pricesRef.off('value');
  }, [firebase]);

  // Fetch Rydio real-time estimate
  useEffect(() => {
    if (!firebase || !firebase.database) return;

    // Get car types for pricing
    const carTypesRef = firebase.database.ref('cartypes');
    carTypesRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const carTypes = Object.values(snapshot.val());
        const standardCar = carTypes.find(c => c.name?.toLowerCase().includes('standard') || c.pos === 0);
        if (standardCar) {
          // Calculate estimate for 18km (Bergen airport to city)
          const baseFare = standardCar.base_fare || 79;
          const ratePerKm = standardCar.rate_per_kilometer || 12;
          const distance = 18;
          const toll = 10;
          const airportFee = 10;
          const estimate = baseFare + (ratePerKm * distance) + toll + airportFee;
          setRydioPrice({
            price: Math.round(estimate),
            base: Math.round(baseFare + (ratePerKm * distance)),
            toll,
            airportFee,
            vehicleType: standardCar.name,
          });
        }
      }
    });

    return () => carTypesRef.off('value');
  }, [firebase]);

  // Fetch pending items for admin
  useEffect(() => {
    if (!firebase || !firebase.database || !isAdmin) return;

    const companiesRef = firebase.database.ref('turpris_companies');
    const pricesRef = firebase.database.ref('turpris_price_submissions');

    companiesRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const companies = Object.entries(snapshot.val())
          .filter(([_, c]) => c.status === 'pending')
          .map(([id, c]) => ({ id, ...c }));
        setPendingCompanies(companies);
      }
    });

    pricesRef.on('value', (snapshot) => {
      if (snapshot.exists()) {
        const prices = Object.entries(snapshot.val())
          .filter(([_, p]) => p.status === 'pending')
          .map(([id, p]) => ({ id, ...p }));
        setPendingPrices(prices);
      }
    });

    return () => {
      companiesRef.off('value');
      pricesRef.off('value');
    };
  }, [firebase, isAdmin]);

  const handleSearch = async () => {
    setLoading(true);

    // Build results from real data
    const results = [];

    // Add Rydio (our service) - always first and featured
    if (rydioPrice) {
      results.push({
        id: 'rydio-standard',
        provider: 'rydio',
        providerName: 'Rydio Taxi',
        vehicleType: rydioPrice.vehicleType || '1-4 Standard',
        price: rydioPrice.price,
        duration: 22,
        distance: 18,
        co2: 1200,
        features: ['Fastpris', 'Elbil tilgjengelig', 'Norsk selskap', 'Book direkte'],
        isEstimate: false,
        priceBreakdown: rydioPrice,
        bookingUrl: '/addbookings',
      });
    }

    // Add competitor prices from Firebase
    if (competitorPrices['Uber']) {
      competitorPrices['Uber'].forEach((p, i) => {
        results.push({
          id: `uber-${i}`,
          provider: 'uber',
          providerName: 'Uber',
          vehicleType: p.category || 'UberX',
          price: p.price,
          duration: 25,
          distance: 18,
          co2: 2800,
          features: ['App-bestilling'],
          isEstimate: true,
          priceBreakdown: { base: p.price - 42, toll: 32, airportFee: 10 },
        });
      });
    }

    if (competitorPrices['Bolt']) {
      competitorPrices['Bolt'].forEach((p, i) => {
        results.push({
          id: `bolt-${i}`,
          provider: 'bolt',
          providerName: 'Bolt',
          vehicleType: p.category || 'Bolt',
          price: p.price,
          duration: 25,
          distance: 18,
          co2: 2800,
          features: ['App-bestilling', 'Ingen surge'],
          isEstimate: true,
          priceBreakdown: { base: p.price - 17, toll: 7, airportFee: 10 },
        });
      });
    }

    if (competitorPrices['Bergen Taxi']) {
      competitorPrices['Bergen Taxi'].forEach((p, i) => {
        results.push({
          id: `bergentaxi-${i}`,
          provider: 'bergen_taxi',
          providerName: 'Bergen Taxi',
          vehicleType: p.category || '1-4 Standard',
          price: p.price,
          duration: 22,
          distance: 18,
          co2: 3200,
          features: ['Tradisjonell taxi'],
          isEstimate: false,
        });
      });
    }

    // Add VIP options
    if (rydioPrice) {
      results.push({
        id: 'rydio-vip',
        provider: 'rydio_vip',
        providerName: 'Rydio VIP',
        vehicleType: 'Executive',
        price: Math.round(rydioPrice.price * 1.8),
        duration: 22,
        distance: 18,
        co2: 1500,
        features: ['Premium kjøretøy', 'Profesjonell sjåfør', 'Gratis vann', 'WiFi'],
        isEstimate: false,
        priceBreakdown: { base: Math.round(rydioPrice.base * 1.8), toll: rydioPrice.toll, airportFee: rydioPrice.airportFee * 2 },
        bookingUrl: '/addbookings',
      });
    }

    // Sort by price
    results.sort((a, b) => a.price - b.price);

    setSearchResults(results);
    setLoading(false);
  };

  const handleSwapLocations = () => {
    const temp = origin;
    setOrigin(destination);
    setDestination(temp);
  };

  // Company registration
  const handleCompanySubmit = async () => {
    if (!firebase || !firebase.database) return;

    try {
      const companiesRef = firebase.database.ref('turpris_companies');
      await companiesRef.push({
        ...companyForm,
        status: 'pending',
        submittedBy: auth?.profile?.uid,
        submittedAt: new Date().toISOString(),
      });

      setSnackbar({ open: true, message: 'Søknad sendt! Vi vil vurdere den snart.', severity: 'success' });
      setCompanyDialog(false);
      setCompanyForm({ name: '', orgNumber: '', contactEmail: '', contactPhone: '', description: '', serviceTypes: [] });
    } catch (error) {
      setSnackbar({ open: true, message: 'Feil ved innsending: ' + error.message, severity: 'error' });
    }
  };

  // Image upload and OCR
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setPriceImage(file);
    setExtracting(true);

    try {
      // Upload to Firebase Storage
      const imageRef = firebase.storage.ref(`turpris_submissions/${Date.now()}_${file.name}`);
      await imageRef.put(file);
      const imageUrl = await imageRef.getDownloadURL();

      // Call OCR Cloud Function
      const response = await fetch(
        `https://us-central1-reaplnprge.cloudfunctions.net/googleapis-extract_kiosk_prices`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setExtractedPrices({
          ...result,
          imageUrl,
        });
      } else {
        setSnackbar({ open: true, message: 'Kunne ikke lese priser fra bildet', severity: 'warning' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Feil ved opplasting: ' + error.message, severity: 'error' });
    }

    setExtracting(false);
  };

  const handlePriceSubmit = async () => {
    if (!firebase || !firebase.database || !extractedPrices) return;

    try {
      const submissionsRef = firebase.database.ref('turpris_price_submissions');
      await submissionsRef.push({
        ...extractedPrices,
        status: 'pending',
        submittedBy: auth?.profile?.uid,
        submittedByName: `${auth?.profile?.firstName} ${auth?.profile?.lastName}`,
        submittedAt: new Date().toISOString(),
        route: `${origin} → ${destination}`,
      });

      setSnackbar({ open: true, message: 'Priser sendt inn! Admin vil godkjenne dem.', severity: 'success' });
      setSubmitDialog(false);
      setPriceImage(null);
      setExtractedPrices(null);
    } catch (error) {
      setSnackbar({ open: true, message: 'Feil ved innsending: ' + error.message, severity: 'error' });
    }
  };

  // Admin actions
  const handleApproveCompany = async (company) => {
    if (!firebase || !firebase.database) return;

    try {
      const companyRef = firebase.database.ref(`turpris_companies/${company.id}`);
      await companyRef.update({
        status: 'approved',
        approvedBy: auth?.profile?.uid,
        approvedAt: new Date().toISOString(),
      });

      setSnackbar({ open: true, message: `${company.name} er godkjent!`, severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Feil: ' + error.message, severity: 'error' });
    }
  };

  const handleApprovePrices = async (submission) => {
    if (!firebase || !firebase.database) return;

    try {
      // Update submission status
      const submissionRef = firebase.database.ref(`turpris_price_submissions/${submission.id}`);
      await submissionRef.update({
        status: 'approved',
        approvedBy: auth?.profile?.uid,
        approvedAt: new Date().toISOString(),
      });

      // Add prices to competitor_prices
      if (submission.prices) {
        const pricesRef = firebase.database.ref('competitor_prices');
        for (const price of Object.values(submission.prices)) {
          await pricesRef.push({
            ...price,
            source: 'community',
            submittedBy: submission.submittedByName,
            timestamp: new Date().toISOString(),
          });
        }
      }

      setSnackbar({ open: true, message: 'Priser godkjent og publisert!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Feil: ' + error.message, severity: 'error' });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Tur<span style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Pris</span>
        </Typography>
        <Typography color="text.secondary">
          Sammenlign transportpriser i sanntid
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        centered
        sx={{ mb: 3 }}
      >
        <Tab icon={<SearchIcon />} label="Søk" />
        {isCommunity && <Tab icon={<CameraIcon />} label="Send inn priser" />}
        {(isAdmin || isFleetAdmin) && <Tab icon={<BusinessIcon />} label="Selskaper" />}
        {isAdmin && (
          <Tab
            icon={
              <Badge badgeContent={pendingCompanies.length + pendingPrices.length} color="error">
                <AdminIcon />
              </Badge>
            }
            label="Admin"
          />
        )}
      </Tabs>

      {/* Search Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Search Box */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Fra"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                <IconButton onClick={handleSwapLocations} sx={{ bgcolor: 'action.hover' }}>
                  <SwapIcon />
                </IconButton>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Til"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                }}
              >
                Sammenlign priser
              </Button>
            </Box>
          </Paper>

          {/* Results */}
          {searchResults.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {searchResults.length} alternativer funnet
              </Typography>

              <Grid container spacing={2}>
                {searchResults.map((result, index) => {
                  const provider = PROVIDERS[result.provider] || {};
                  const Icon = provider.icon || CarIcon;
                  const isCheapest = index === 0;
                  const isFeatured = result.provider === 'rydio';

                  return (
                    <Grid item xs={12} key={result.id}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: isCheapest ? '2px solid #10b981' : '1px solid',
                          borderColor: isCheapest ? '#10b981' : 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                          {/* Provider Header */}
                          <Box
                            sx={{
                              width: { xs: '100%', sm: 200 },
                              p: 3,
                              background: provider.gradient,
                              color: 'white',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Icon sx={{ fontSize: 40 }} />
                            <Typography variant="h6" fontWeight="bold">{result.providerName}</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>{result.vehicleType}</Typography>
                            {isFeatured && (
                              <Chip
                                icon={<StarIcon sx={{ color: '#fbbf24 !important' }} />}
                                label="Anbefalt"
                                size="small"
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                              />
                            )}
                          </Box>

                          {/* Details */}
                          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <Box>
                              {/* Badges */}
                              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {isCheapest && (
                                  <Chip label="Billigst" color="success" size="small" icon={<CheckIcon />} />
                                )}
                                {result.isEstimate && (
                                  <Chip label="Estimert" size="small" variant="outlined" />
                                )}
                              </Box>

                              {/* Stats */}
                              <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Reisetid</Typography>
                                  <Typography fontWeight="bold">{result.duration} min</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Avstand</Typography>
                                  <Typography fontWeight="bold">{result.distance} km</Typography>
                                </Box>
                                {result.co2 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">CO₂</Typography>
                                    <Typography fontWeight="bold" color="success.main">{(result.co2/1000).toFixed(1)} kg</Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Features */}
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {result.features?.map((f, i) => (
                                  <Chip key={i} label={f} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          </CardContent>

                          {/* Price & CTA */}
                          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', minWidth: 150 }}>
                            <Typography variant="h4" fontWeight="bold">
                              {formatPrice(result.price)}
                            </Typography>
                            {result.priceBreakdown && (
                              <Typography variant="caption" color="text.secondary">
                                Inkl. bom {result.priceBreakdown.toll} kr
                              </Typography>
                            )}
                            <Button
                              variant={isFeatured ? 'contained' : 'outlined'}
                              sx={{
                                mt: 2,
                                ...(isFeatured && {
                                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                }),
                              }}
                              endIcon={<OpenIcon />}
                              href={result.bookingUrl}
                            >
                              {isFeatured ? 'Bestill nå' : 'Velg'}
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Community Submission Tab */}
      {tabValue === 1 && isCommunity && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CameraIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Last opp bilde av prisautomat
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Ta bilde av prisautomaten på flyplassen. Vi ekstraherer prisene automatisk.
          </Typography>

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            sx={{ mb: 2 }}
          >
            Velg bilde
            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </Button>

          {extracting && (
            <Box sx={{ mt: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 1 }}>Leser priser fra bildet...</Typography>
            </Box>
          )}

          {extractedPrices && (
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Fant {Object.keys(extractedPrices.prices || {}).length} priser!
              </Alert>

              <List>
                {Object.entries(extractedPrices.prices || {}).map(([key, price]) => (
                  <ListItem key={key}>
                    <ListItemText
                      primary={`${price.company} - ${price.category}`}
                      secondary={`${price.price} kr`}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => { setPriceImage(null); setExtractedPrices(null); }}>
                  Avbryt
                </Button>
                <Button variant="contained" onClick={handlePriceSubmit}>
                  Send inn for godkjenning
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Companies Tab */}
      {tabValue === 2 && (isAdmin || isFleetAdmin) && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5">Registrerte selskaper</Typography>
            <Button variant="contained" startIcon={<BusinessIcon />} onClick={() => setCompanyDialog(true)}>
              Registrer selskap
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Transportselskaper kan registrere seg for å få sine priser vist i sammenligningen.
          </Alert>

          {/* Company list would go here */}
        </Box>
      )}

      {/* Admin Tab */}
      {tabValue === 3 && isAdmin && (
        <Box>
          <Typography variant="h5" gutterBottom>Ventende godkjenninger</Typography>

          {/* Pending Companies */}
          {pendingCompanies.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Selskaper ({pendingCompanies.length})</Typography>
              <List>
                {pendingCompanies.map((company) => (
                  <ListItem key={company.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'warning.main' }}>
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={company.name}
                      secondary={`Org.nr: ${company.orgNumber} | ${company.contactEmail}`}
                    />
                    <ListItemSecondaryAction>
                      <Button color="success" onClick={() => handleApproveCompany(company)}>
                        Godkjenn
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Pending Price Submissions */}
          {pendingPrices.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>Prisinnmeldinger ({pendingPrices.length})</Typography>
              <List>
                {pendingPrices.map((submission) => (
                  <ListItem key={submission.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 2 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'info.main' }}>
                        <CameraIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${submission.route}`}
                      secondary={`Fra: ${submission.submittedByName} | ${Object.keys(submission.prices || {}).length} priser`}
                    />
                    <ListItemSecondaryAction>
                      <Button color="success" onClick={() => handleApprovePrices(submission)}>
                        Godkjenn
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {pendingCompanies.length === 0 && pendingPrices.length === 0 && (
            <Alert severity="success">Ingen ventende godkjenninger!</Alert>
          )}
        </Box>
      )}

      {/* Company Registration Dialog */}
      <Dialog open={companyDialog} onClose={() => setCompanyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrer transportselskap</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Selskapsnavn"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organisasjonsnummer"
                value={companyForm.orgNumber}
                onChange={(e) => setCompanyForm({ ...companyForm, orgNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kontakt e-post"
                value={companyForm.contactEmail}
                onChange={(e) => setCompanyForm({ ...companyForm, contactEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kontakt telefon"
                value={companyForm.contactPhone}
                onChange={(e) => setCompanyForm({ ...companyForm, contactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Beskrivelse"
                value={companyForm.description}
                onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCompanySubmit}>Send søknad</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
